<template>
  <div class="shop-layout">
    <overlay v-if="overlayActive" :layer-index="true" />
    <loader />
    <div id="viewport" class="w-100 relative viewport-wrapper">
      <main-header :layer-index="true" />
      <section class="partner-block">
        <header-navigation />
        <category-burger-menu :is-categories-loaded="isCategoriesLoaded" :categories="tree" />
        <overlay-content />
        <async-sidebar
          :async-component="SearchPanel"
          :is-open="isSearchPanelOpen"
          @close="$store.commit('ui/setSearchpanel')"
          direction="bottom"
        />
        <async-sidebar
          :async-component="Microcart"
          :is-open="isMicrocartOpen"
          :layer-index="true"
          @close="$store.commit('ui/setMicrocart')"
        />
        <async-sidebar
          :async-component="SidebarMenu"
          :is-open="isSidebarOpen"
          @close="$store.commit('ui/setSidebar')"
          direction="left"
        />
        <async-sidebar
          :async-component="Wishlist"
          :is-open="isWishlistOpen"
          @close="$store.commit('ui/setWishlist')"
        />
        <div class="partner-block-inner">
          <aside-sidebar />
          <slot></slot>
        </div>
      </section>
      <main-footer />
      <notification />
      <sign-up />
      <age-popup />
      <cookie-notification />
      <offline-badge />
      <order-confirmation :orders-data="ordersData" v-if="loadOrderConfirmation" />
      <schedule-modal class="modal-schedule"/>
    </div>
    <vue-progress-bar />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import OverlayContent from 'theme/components/core/blocks/Shop/Overlay'
import CategoryBurgerMenu from 'theme/components/core/blocks/Shop/Navigation/CategoryBurgerMenu'
import HeaderNavigation from 'theme/components/core/blocks/Shop/Header'
import AsideSidebar from 'theme/components/core/blocks/Shop/Sidebar'
import AsyncSidebar from 'theme/components/theme/blocks/AsyncSidebar/AsyncSidebar.vue'
import MainHeader from 'theme/components/core/blocks/Header/Header.vue'
import MainFooter from 'theme/components/theme/blocks/Footer/Footer.vue'
import Overlay from 'theme/components/core/Overlay.vue'
import Loader from 'theme/components/core/Loader.vue'
import Notification from 'theme/components/core/Notification.vue'
import SignUp from 'theme/components/core/blocks/Auth/SignUp.vue'
import AgePopup from 'theme/components/core/blocks/Category/AgeConfirmation.vue'
import CookieNotification from 'theme/components/core/CookieNotification.vue'
import OfflineBadge from 'theme/components/core/OfflineBadge.vue'
import { isServer } from '@vue-storefront/core/helpers'
import Head from 'theme/head'
import config from 'config'

const SidebarMenu = () => import('theme/components/core/blocks/SidebarMenu/SidebarMenu.vue')
const Microcart = () => import('theme/components/core/blocks/Microcart/Microcart.vue')
const Wishlist = () => import('theme/components/core/blocks/Wishlist/Wishlist.vue')
const SearchPanel = () => import('theme/components/core/blocks/SearchPanel/SearchPanel.vue')
const OrderConfirmation = () => import('theme/components/core/blocks/Checkout/OrderConfirmation.vue')
import ScheduleModal from "../components/core/blocks/Shop/Navigation/ScheduleModal";

export default {
  components: {
    MainHeader,
    MainFooter,
    Overlay,
    Loader,
    Notification,
    SignUp,
    AgePopup,
    CookieNotification,
    OfflineBadge,
    OrderConfirmation,
    HeaderNavigation,
    AsideSidebar,
    CategoryBurgerMenu,
    AsyncSidebar,
    OverlayContent,
    ScheduleModal
  },
  data () {
    return {
      loadStoreModal: false,
      loadOrderConfirmation: false,
      ordersData: [],
      isCategoriesLoaded: false,
      Microcart,
      Wishlist,
      SearchPanel,
      SidebarMenu
    }
  },
  metaInfo: Head,
  beforeMount () {
    // Progress bar on top of the page
    this.$router.beforeEach((to, from, next) => {
      this.$Progress.start()
      this.$Progress.increase(40)
      next()
    })
    this.$router.afterEach((to, from) => {
      this.$Progress.finish()
    })
    this.$bus.$on('offline-order-confirmation', this.onOrderConfirmation)
    this.$bus.$on('success-authenticate', this.goToStoredRoadAfterLogin)
  },
  mounted () {
    if (this.currentPartner) {
      this.getTree({ partner_code: this.currentPartner.code, location_code: this.currentPartner.location_code })
        .finally(() => this.isCategoriesLoaded = true)
    }
  },
  watch: {
    '$route.params.store': {
      handler: function () {
        if (this.currentPartner) {
          this.isCategoriesLoaded = false
          this.getTree({ partner_code: this.currentPartner.code, location_code: this.currentPartner.location_code })
            .finally(() => this.isCategoriesLoaded = true)
        }
      }
    }
  },
  computed: {
    ...mapState({
      overlayActive: state => state.ui.overlay,
      isSearchPanelOpen: state => state.ui.searchpanel,
      isSidebarOpen: state => state.ui.sidebar,
      isMicrocartOpen: state => state.ui.microcart,
      isWishlistOpen: state => state.ui.wishlist
    }),
    ...mapGetters({
      currentPartner: 'partners/getCurrentPartner',
      currentLocation: 'locations/getActiveLocation',
      getPartnerCategoryTree: 'category/getPartnerCategoryTree'
    }),
    setRootName () {
      return this.$route.name
    },
    isOrderPage () {
      return (this.$route.name === 'my-order' || this.$route.name === 'my-orders' || this.$route.name === 'my-account' || this.$route.name === 'my-shipping-details')
    },
    rootHome () {
      return this.$route.name === 'selected-store'
    },
    rootMain () {
      return this.$route.name === 'home'
    },
    rootNameAllDiscounts () {
      return this.$route.name === 'allDiscounts'
    },
    isThankYouPage () {
      return this.$store.state.checkout.isThankYouPage ? this.$store.state.checkout.isThankYouPage : false
    },
    isPartnerPages () {
      if (this.currentPartner) {
        let url = '/' + this.currentPartner.location_code + '/' + this.currentPartner.code
        return this.$route.path === url
      }
      return false
    },
    tree () {
      return this.getPartnerCategoryTree(`${this.currentPartner.location_code}_${this.currentPartner.code}`)
    }
  },
  methods: {
    ...mapActions({
      getTree: 'category/getPartnerCategoryTree'
    }),
    onOrderConfirmation (payload) {
      this.loadOrderConfirmation = true
      this.ordersData = payload
      this.$bus.$emit('modal-show', 'modal-order-confirmation')
    },
    fetchMenuData () {
      return this.$store.dispatch('category/list', { level: config.entities.category.categoriesDynamicPrefetch && config.entities.category.categoriesDynamicPrefetchLevel >= 0 ? config.entities.category.categoriesDynamicPrefetchLevel : null, includeFields: config.entities.optimize && isServer ? config.entities.category.includeFields : null, skipCache: isServer })
    },
    goToStoredRoadAfterLogin () {
      if (localStorage.getItem('redirect')) {
        location.replace(localStorage.getItem('redirect'));
        localStorage.removeItem('redirect');
      }
    }
  },
  serverPrefetch () {
    if (this.$route.name === 'category') {
      return this.fetchMenuData()
    }
  },
  beforeDestroy () {
    this.$bus.$off('offline-order-confirmation', this.onOrderConfirmation)
    this.$bus.$off('success-authenticate', this.goToStoredRoadAfterLogin)
  }
}
</script>

<style lang="scss" src="theme/css/main.scss"></style>
<style lang="scss" scoped>
.viewport-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.partner-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;

  &-inner {
    width: 1140px;

    display: flex;
    justify-content: space-between;

    @media only screen and (max-width: 1200px) {
      width: 946px;
      padding: 0 15px;
    }

    @media only screen and (max-width: 991px) {
      width: 722px;
    }

    @media only screen and (max-width: 768px) {
      width: 530px;
    }

    @media only screen and (max-width: 576px) {
      width: 100%;
    }
  }
}

.partner-block-inner {
  column-gap: 20px;
}

.modal-schedule {
  z-index: 1000;
}
</style>

<style lang="scss">
  ._wrapper {
    flex: 1 1 auto;
  }
</style>
